import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { useMemo } from 'react';
import { useHandlers } from '../../../context/page-context';
import { parseSemver } from '../../../lib/semver';
import type { SparkJsVersionData } from '../api';
import { SPARKJS_LATEST_VERSION } from '../constants';

export type SparkJsVersionOption = {
  value: string;
  label: string;
};

const QK_SPARKJS_VERSIONS = () => ['sparkjs-versions'] as const;

function buildVersionOptions(versions: SparkJsVersionData[]): SparkJsVersionOption[] {
  const options: SparkJsVersionOption[] = [];

  [...versions]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .forEach((versionData) => {
      const semver = parseSemver(versionData.version);
      const label =
        versionData.version === SPARKJS_LATEST_VERSION
          ? t('features:storefront_widgets.components.sparkjs_version_card.latest_label')
          : semver
            ? `${semver.major}.${semver.minor}`
            : versionData.version;

      const existing = options.find((option) => option.label === label);
      if (!existing) {
        options.push({ value: versionData.version, label });
        return;
      }

      const existingSemver = parseSemver(existing.value);
      if (semver && existingSemver && semver.patch > existingSemver.patch) {
        existing.value = versionData.version;
      }
    });

  return options;
}

export function useSparkJsVersions() {
  const { apis } = useHandlers();

  const query = useQuery({
    queryKey: QK_SPARKJS_VERSIONS(),
    queryFn: () => apis.sparkJs.versions(),
  });

  const options = useMemo(() => buildVersionOptions(query.data?.versions ?? []), [query.data]);

  return { ...query, options };
}
